/**
 * The view-model of a run **in time** — `S4` and `S5`.
 *
 * Pure TS: no React, no Studio, no DOM. Sibling of `practice.ts`, which does
 * the same job for the three runs without rhythm.
 *
 * ## What this step asks, and what it used to ask
 *
 * S4 used to be graded: two clean passes in a row, at most two errors each,
 * and **zero** missed note. Ported to Python and replayed over the real work
 * units (PDMX testset 200 → 10 528 units), that gate:
 *
 * - killed **100%** of passes on a single forgotten note (0 / 10 528);
 * - failed **15.0%** of units at ordinary human jitter (σ = 100 ms, well
 *   inside its own ±467 ms window) — and **98.5% of those failures (1 553 /
 *   1 577) contained no out-of-window error at all**. They were artefacts of
 *   attributing a press to the nearest group *of the same pitch*, and 22.8%
 *   of notes (67 104 / 293 967) repeat a pitch within a beat;
 * - passed **62.2%** of units played with **no rhythm whatsoever** (equal
 *   intervals), and still 40.1% (2 672 / 6 656) of the units that actually
 *   carry two or more distinct rhythmic values;
 * - passed **84.7%** of units with two consecutive notes swapped.
 *
 * A gate that fails a clean player, passes a player with no rhythm, and never
 * checks the order is not measuring what it claims. It is gone.
 *
 * > **What this step teaches is that the hands do not stop.** A wrong note
 * > that does not break the flow is progress; the previous rule made it a
 * > failure on every single pass.
 *
 * So a pass has exactly one adverse event — `Interruption`, the player
 * *stopped* — and it is detected on silence, not on pitch. Everything else is
 * reported as a fact and never as a verdict: which onsets were honoured (the
 * run map draws them), and whether the player's own pulse was steady.
 *
 * ## Tempo is a setting, not a constant
 *
 * `TEMPO_S4_FRACTION = 0.6` was global. What a player actually endures is not
 * the tempo, it is the **rate of attacks**: at 60% it spreads over a factor
 * of 4.6 across the corpus (0.97 → 4.48 attacks/s, p10 → p90 on the testset;
 * 2.00 → 7.69 on the repository's own catalogue). **97.6%** of testset units
 * and 97.8% of the repository's need something other than 0.6. So the
 * starting rate is computed from the unit's density, and the player moves it.
 */

import type {
  HandsAsked,
  Phrase,
  RecordedPress,
} from '@/lib/learning-journey/types';
import { buildPracticeGroups, type PracticeGroup } from './practice';

/**
 * What a reading is a reading **of**.
 *
 * A finished pass is kept on screen so the player can read what it said, and
 * it was kept across a change of subject: arriving on the right hand of a
 * unit whose left hand had just been played, or on a brand new unit, the
 * panel opened on « Tu es allé au bout », a stray count and a tempo offer,
 * for a passage the player had never touched. The step announced itself as
 * already done.
 *
 * The reading is not stale in the sense of old: it is *about something else*,
 * and the tell was on screen — « 3 notes à côté, sur 7 attaques » under a
 * rail of nineteen cells. So the run holds the subject its reading belongs
 * to, and drops the reading when the subject changes.
 *
 * The span is what tells them apart, and it does two jobs now: it separates
 * one unit from the next, and it separates a whole pass from a pass the
 * player asked to resume part-way through by scrolling the roll. The hands
 * used to be part of the key, back when the step walked a ladder of them.
 */
export function runSubject(
  range: Pick<Phrase, 'id' | 'startTick' | 'endTick'>,
  step: string,
): string {
  return `${step}:${range.id}:${range.startTick}-${range.endTick}`;
}

// ── The onsets ────────────────────────────────────────────────────

/**
 * The attack instants of the run, in order, each carrying its hands.
 *
 * Deliberately the **same objects** the no-rhythm runs use: an onset here is
 * a group of S3 with a time attached. Sharing them is what lets the run map
 * of S3 draw the same silhouette here, and it removes the second, subtly
 * different grouping that `buildExpectedSequence` maintained in parallel.
 *
 * Always both hands. It took a mode, back when the run in time was walked one
 * hand at a time; on a one-handed unit the `S3` grouping already yields that
 * hand alone, so nothing is lost with the ladder.
 */
export function rhythmGroups(
  phrase: Pick<Phrase, 'notesByHand'>,
): readonly PracticeGroup[] {
  return buildPracticeGroups(phrase, 'S3');
}

/**
 * The hands this unit actually asks for.
 *
 * A fact read off the phrase, and display only: a passage where one hand
 * never plays a note must not announce itself as « mains ensemble » and light
 * two pills, which is the plainest kind of untrue a step can say about
 * itself.
 */
export function handsAsked(
  phrase: Pick<Phrase, 'notesByHand'>,
): HandsAsked {
  const hasLeft = phrase.notesByHand.left.length > 0;
  const hasRight = phrase.notesByHand.right.length > 0;

  if (hasLeft && !hasRight) {
    return 'lh-only';
  }

  if (hasRight && !hasLeft) {
    return 'rh-only';
  }

  return 'both';
}

// ── What a rung has cost so far ───────────────────────────────────

/**
 * How the current rung has gone, in the only terms the journey keeps: how
 * many finished passes did not land, and whether one finally did.
 *
 * It exists for one moment. A player who has stopped in the middle five
 * times and finally goes through, under the right keys, has just crossed the
 * only line that matters at this step, and the panel said exactly what it
 * says on the fifth easy pass of a unit they never struggled with: « Tu es
 * allé au bout. » Then it led with the keys they hit beside. Nothing on the
 * screen noticed, and the player did.
 *
 * This is not a score and does not become one. Nothing is counted back to
 * the player, no number is shown, nothing is withheld until it is earned:
 * the count lives here only to tell a first landing apart from an ordinary
 * one, and it is forgotten with the rung.
 */
export type RungStory = {
  /** Finished passes that did not land. See `landedPass`. */
  readonly tries: number;
  /** A pass had already landed at this rung before the current one. */
  readonly landed: boolean;
  /** The pass that just finished landed. */
  readonly held: boolean;
  /** The pass that just finished is the one that turned it around. */
  readonly salute: boolean;
};

export const NEW_RUNG: RungStory = {
  tries: 0,
  landed: false,
  held: false,
  salute: false,
};

/**
 * Passes that have to have gone by before a landing is worth marking.
 *
 * Two, so an ordinary hiccup does not trigger it. One stumble then success
 * is the normal shape of learning something easy, and a fanfare on it would
 * make the sentence worthless the day it is deserved.
 */
const EARNED_TRIES = 2;

/** A pass that did not land: stopped, holes, or keys beside. */
export function afterMiss(story: RungStory): RungStory {
  return {
    tries: story.tries + 1,
    landed: story.landed,
    held: false,
    salute: false,
  };
}

/** A pass that landed. See `landedPass`. */
export function afterLanding(story: RungStory): RungStory {
  return {
    tries: story.tries,
    landed: true,
    held: true,
    salute: !story.landed && story.tries >= EARNED_TRIES,
  };
}

/**
 * The headline of the card a finished pass leaves behind.
 *
 * « Tu l'as » and not « bravo »: the sentence names what happened, in the
 * second person, and lets the player supply the feeling they already have.
 *
 * `ordinary` is what the step says the rest of the time, which is not the
 * same sentence everywhere: going through a unit in time and joining two
 * units are two different things to have done. The salute is one sentence
 * for both, because what it marks is not the task.
 */
export function arrivalTitle(story: RungStory, ordinary: string): string {
  if (story.salute) {
    return 'Ça y est, tu l’as.';
  }

  // Read only after a pass that reached the end, since a pass that stopped
  // gets its own screen. So what is left here is a crossing with holes in it,
  // and the card used to call that « tu es allé au bout » exactly as it does
  // a pass that held. Not stopping is the contract of the step and is worth
  // saying; it is not worth saying as if it were the passage.
  if (!story.held) {
    return 'Tu n’as pas lâché.';
  }

  return ordinary;
}

// ── Tempo ─────────────────────────────────────────────────────────

/**
 * The rungs the player steps through, as fractions of the written tempo.
 *
 * Geometric-ish, roughly 10-14% apart: below that a step is inaudible and the
 * control feels broken, above it a step is a different piece. The top rung is
 * the written tempo and not a lower ceiling — phase 2 exists to *hold* the
 * target tempo over the whole piece, not to be the first place it is allowed.
 */
export const RATE_LADDER: readonly number[] = [
  0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65, 0.7, 0.8, 0.9, 1,
];

/**
 * Attacks per second the starting rate aims for.
 *
 * This number is a **hypothesis**, and it is the only one here: what is
 * measured is that no constant fraction can work (see the file header). It is
 * kept in one place so it can be retuned without re-reading the corpus.
 */
const TARGET_ATTACKS_PER_SECOND = 2;

/**
 * No unit ever *opens* above this fraction of the written tempo.
 *
 * The density rule alone says a passage at two attacks per second is already
 * comfortable, and hands the written tempo to a player meeting the passage in
 * time for the first time: an Ode to Joy in quarter notes at 120 lands exactly
 * on the target, so the step opened at 100% on the first unit a beginner ever
 * plays. Density says what a passage costs to *play*; it says nothing about
 * what it costs to play it for the first time, and the first pass is the one
 * this rate is for. The ladder is right there to climb, and every rung of it
 * is earned by a pass that held.
 *
 * A ceiling, not a target: a dense passage still opens below it.
 *
 * A fraction, though, bites hardest on the slowest pieces: 60% of a ballad
 * written at 65 is 39 BPM, a pulse no hand plays *in* any more, and it was
 * offered on a passage of one chord per bar. So it never holds a unit under
 * `MIN_OPENING_BPM`. See `openingCeiling`.
 */
const MAX_OPENING_RATE = 0.6;

/**
 * The slowest tempo the opening ceiling may hold a unit at.
 *
 * Not a tempo the step opens at: the unit's density and the pace the player
 * held still decide under the ceiling. It only stops the ceiling itself from
 * turning a slow piece into one too slow to be felt as a pulse.
 */
const MIN_OPENING_BPM = 60;

/**
 * Below this, one click per beat stops reading as a pulse and starts reading
 * as isolated events, so the metronome subdivides instead. The band in which
 * a periodic sound is heard *as* a beat bottoms out around here.
 */
const SLOW_PULSE_BPM = 50;

/**
 * The fastest rate a first pass in time may open at, on a piece written at
 * `bpm`: `MAX_OPENING_RATE`, raised on a slow piece to the first rung that
 * reaches `MIN_OPENING_BPM`.
 *
 * At 120 BPM nothing changes — 60% is 72. At 65 the ceiling is the written
 * tempo, where 60% was 39.
 */
export function openingCeiling(bpm: number): number {
  if (bpm <= 0) {
    return MAX_OPENING_RATE;
  }

  const floor = Math.min(1, MIN_OPENING_BPM / bpm);
  const rung = RATE_LADDER.find((candidate) => candidate >= floor) ?? 1;

  return Math.max(MAX_OPENING_RATE, rung);
}

/** The rate this unit opens at, snapped to a rung of the ladder. */
export function startingRate(
  groups: readonly PracticeGroup[],
  bpm: number,
  ppq: number,
): number {
  const perSecondAtFullSpeed = attacksPerSecond(groups, bpm, ppq, 1);

  // Too little to measure a density: the ceiling is all there is to go on.
  if (perSecondAtFullSpeed <= 0) {
    return openingCeiling(bpm);
  }

  return Math.min(
    snapToLadder(TARGET_ATTACKS_PER_SECOND / perSecondAtFullSpeed),
    openingCeiling(bpm),
  );
}

/**
 * The rate a unit opens at, whether or not it has been held before.
 *
 * `held` is `LearningJourney.unitRate`: the **fastest** the unit has ever been
 * played through at, which is a record and not a working tempo. Opening on it
 * put a player who had once climbed to 80% in front of « On commence à 96 »
 * the next time they came back, cold, before a single pass. That is the
 * mistake `MAX_OPENING_RATE` exists to prevent, arriving by the other door:
 * density says what a passage costs to play, a record says what it cost once,
 * and neither says what it costs to play it *now*.
 *
 * So a unit already held opens **one notch below** it, using the same notch
 * the tempo offer uses (`stepRate` down, which drops two rungs). One pass
 * that lands puts the rung back, and the ladder is right there. Nothing is
 * lost: `unitRate` still remembers the record, and the chaining step still
 * opens on it.
 *
 * A unit never held falls back to the density rule, unchanged.
 */
export function unitOpeningRate(
  held: number | null,
  groups: readonly PracticeGroup[],
  bpm: number,
  ppq: number,
): number {
  if (held === null) {
    return startingRate(groups, bpm, ppq);
  }

  return stepRate(held, -1);
}

/**
 * Attacks per second the run would ask for at `rate`, or `0` when the unit is
 * too short to have a density (a single onset has no interval).
 */
export function attacksPerSecond(
  groups: readonly PracticeGroup[],
  bpm: number,
  ppq: number,
  rate: number,
): number {
  if (groups.length < 2 || bpm <= 0 || ppq <= 0 || rate <= 0) {
    return 0;
  }

  const spanTicks = groups[groups.length - 1].startTick - groups[0].startTick;

  if (spanTicks <= 0) {
    return 0;
  }

  const attacksPerBeat = (groups.length - 1) / (spanTicks / ppq);

  return (attacksPerBeat * bpm * rate) / 60;
}

/**
 * Two rungs down at a time; one rung up. Clamped at both ends.
 *
 * The asymmetry is the asymmetry of the two situations, which the offer
 * itself already reflects: slowing down is a move made *because the passage
 * did not come out*, speeding up one made because it did.
 *
 * A single rung down was not an answer to the first. From the 0.6 ceiling a
 * unit opens at, one rung is 72 → 66 BPM on a piece written at 120: 8%, which
 * changes nothing a hand can feel, so the offer read as a control that did
 * nothing. Two rungs is 72 → 60, a sixth off, which is a different passage to
 * play. Going back up stays one rung: the ladder is the progression, and it
 * is worth crossing having actually played every rung of it.
 */
export function stepRate(rate: number, direction: -1 | 1): number {
  const index = RATE_LADDER.indexOf(snapToLadder(rate));
  const step = direction === -1 ? -2 : 1;
  const next = Math.min(
    RATE_LADDER.length - 1,
    Math.max(0, index + step),
  );

  return RATE_LADDER[next];
}

/** The tempo the player is actually playing at, in whole BPM. */
export function bpmAt(bpm: number, rate: number): number {
  return Math.max(1, Math.round(bpm * rate));
}

/**
 * How the metronome should divide the beat at this tempo.
 *
 * Two decisions that were one: *how fast* comes from the density of the unit,
 * *how the pulse is voiced* comes from the resulting tempo. Slowing a piece
 * far enough to read it can drop the beat below the band where it is felt as
 * a beat at all; subdividing keeps the pulse alive without speeding the piece
 * back up.
 */
export function pulseDivision(effectiveBpm: number): 'quarter' | 'eighths' {
  return effectiveBpm < SLOW_PULSE_BPM ? 'eighths' : 'quarter';
}

/** The rung of `RATE_LADDER` nearest to `rate`. */
export function snapToLadder(rate: number): number {
  let best = RATE_LADDER[0];

  for (const rung of RATE_LADDER) {
    if (Math.abs(rung - rate) < Math.abs(best - rate)) {
      best = rung;
    }
  }

  return best;
}

// ── What happened during the pass ─────────────────────────────────

/** Onset positions, in wall-clock ms, for a run anchored at `anchorMs`. */
export type RunClock = {
  /** Instant the playhead sat on the first tick of the run. */
  readonly anchorMs: number;
  /** Real duration of one beat at the run's tempo. */
  readonly beatMs: number;
  /** Ticks per quarter note of the song. */
  readonly ppq: number;
};

/**
 * Silence long enough to read as "the hands left the keys", in beats.
 *
 * Two beats, and never on its own: a gap only means a stop if the score was
 * asking for something inside it. A unit with one attack per bar is silent
 * for three beats at a time by construction.
 */
const STOP_SILENCE_BEATS = 2;

/**
 * Onsets that have to fall inside that silence before it counts.
 *
 * One skipped note is not a stop — it is the exact thing this step tells the
 * player to keep going through. Two consecutive ones are.
 */
const STOP_MISSED_ONSETS = 2;

/** How far from its instant a press may be and still belong to an onset. */
const HONOURED_BEATS = 1;

/**
 * How many distinct note values the unit carries, quantised to a sixteenth.
 *
 * The one number that decides whether beating the rhythm out on a single key
 * teaches anything: **36.8% of units (3 875 / 10 530) carry a single value**,
 * where the pulse *is* the rhythm and there is nothing to dissociate. The
 * median is 2 and the p90 is 4. Offering the act to everyone would hand an
 * empty exercise to one unit in three.
 */
export function rhythmicValues(
  groups: readonly PracticeGroup[],
  ppq: number,
): number {
  if (groups.length < 2 || ppq <= 0) {
    return 0;
  }

  const unit = ppq / 4;
  const seen = new Set<number>();

  for (let i = 1; i < groups.length; i += 1) {
    const gap = groups[i].startTick - groups[i - 1].startTick;

    if (gap > 0) {
      seen.add(Math.round(gap / unit));
    }
  }

  return seen.size;
}

/** Where the player came off the rails, if they did. */
export type Interruption = {
  /** Index in `groups` of the first onset that went by in the silence. */
  readonly groupIndex: number;
  readonly startTick: number;
};

/** What a finished pass has to say for itself. */
export type RunReading = {
  /** One flag per onset: was any of its keys struck near its instant. */
  readonly honoured: readonly boolean[];
  /**
   * Every place the player came off the rails, in order. Empty when they
   * went through.
   *
   * All of them and not the first: on a unit of twelve attacks naming one
   * place is the whole report, but a pass over a whole piece runs to 531
   * attacks in the median and the map has to draw what actually happened.
   */
  readonly interruptions: readonly Interruption[];
  /** `null` when there is not enough to read a pulse from — see below. */
  readonly pulse: PulseReading | null;
  /**
   * Keys struck that the passage asks for nowhere near their instant.
   *
   * A fact about pitch, and the only one the reading carries. Empty on a
   * tapped pass, where any key is the right one.
   */
  readonly strays: readonly RecordedPress[];
};

/**
 * Whether the pass crossed the passage.
 *
 * No stop, **and something answered**. The second half is not redundant:
 * `STOP_MISSED_ONSETS` asks for two missed onsets in a row before calling a
 * silence a stop, which is right on a long run and vacuous on a short one — a
 * unit whose hand carries a **single** onset can never satisfy it, so a pass
 * where the player touched nothing at all came back with no stop and was read
 * as a traversal. Measured on the corpus, a hand with fewer than two onsets
 * in a unit is not exotic: a left hand holding one bass note under a phrase
 * is the ordinary case.
 *
 * Lowering `STOP_MISSED_ONSETS` would trade a measured threshold against an
 * edge it was never about. The truth is simpler and holds at every length:
 * nothing played is not a crossing.
 */
export function wentThrough(reading: RunReading): boolean {
  return reading.interruptions.length === 0 && reading.honoured.some(Boolean);
}

/**
 * Share of attacks that must be answered for a stretch to read as played.
 *
 * A convention, and the only one here — no measurement fixes it. It is
 * calibrated to be **unilateral**: `honoured` is measured with a ±1 beat
 * window that does not consume presses, and 61.7% of attacks share a pitch
 * with a neighbour within a beat, so the figure is inflated upwards and never
 * downwards. Simulated on the real attack grids, a player answering 78% of
 * attacks measures 90.1%, and one answering all of them measures 100% at any
 * tempo. **A clean player can therefore never fail this**, and it separates
 * "they played the piece" from "they played something else" without becoming
 * an exam again. If it ever moves, it moves down.
 */
export const PLAYED_THROUGH_RATIO = 0.9;

/**
 * Whether the pass **landed**: crossed the passage, with the notes.
 *
 * Three claims, and the step needs all three. `wentThrough` says the player
 * did not stop, which is the contract of the step and deliberately cheap.
 * `PLAYED_THROUGH_RATIO` says the attacks were answered rather than skated
 * over. `playedClean` says the keys were the right ones, within a tolerance
 * that scales with the length of the run.
 *
 * The middle one was missing from phase 1, and only from phase 1: phase 2 has
 * asked for it since it existed (`heldThrough` in `piece.ts`). A pass that
 * dropped a third of the notes without ever stopping therefore counted as a
 * repetition here, filled a dot of the four the card offers, and at the
 * fourth turned the primary button into « Continuer » — and, with the hands
 * on the piano, moved on by itself. The player knew perfectly well they had
 * not played it. Nothing else on the screen did.
 *
 * This does not restore a gate: moving on is one click away at every moment,
 * and it is the ratio's own calibration that makes it safe. A clean player
 * cannot fail it.
 */
export function landedPass(reading: RunReading): boolean {
  return (
    wentThrough(reading) &&
    honouredRatio(reading) >= PLAYED_THROUGH_RATIO &&
    playedClean(reading)
  );
}

/** Share of onsets that were answered, in `[0, 1]`. `1` on an empty pass. */
export function honouredRatio(reading: RunReading): number {
  if (reading.honoured.length === 0) {
    return 1;
  }

  return (
    reading.honoured.filter(Boolean).length / reading.honoured.length
  );
}

/**
 * Read a finished pass.
 *
 * `presses` is the run's capture, `groups` its onsets. Nothing here is a
 * verdict: `honoured` is drawn, `interruption` is named, `pulse` is spoken
 * only when it is certain.
 */
export function runReading(
  groups: readonly PracticeGroup[],
  presses: readonly RecordedPress[],
  clock: RunClock,
  firstTick: number,
  opts: { readonly ignorePitch?: boolean } = {},
): RunReading {
  const at = onsetTimes(groups, clock, firstTick);
  const ordered = [...presses].sort((a, b) => a.tMs - b.tMs);
  const ignorePitch = opts.ignorePitch === true;

  return {
    honoured: honouredOnsets(groups, ordered, at, clock, ignorePitch),
    interruptions: findInterruptions(groups, ordered, at, clock, ignorePitch),
    pulse: readPulse(groups, ordered, at, clock, ignorePitch),
    strays: strayPresses(groups, ordered, at, clock, ignorePitch),
  };
}

/** Wall-clock instant of each onset. */
export function onsetTimes(
  groups: readonly PracticeGroup[],
  clock: RunClock,
  firstTick: number,
): readonly number[] {
  if (clock.ppq <= 0) {
    return groups.map(() => clock.anchorMs);
  }

  return groups.map(
    (group) =>
      clock.anchorMs +
      ((group.startTick - firstTick) / clock.ppq) * clock.beatMs,
  );
}

/**
 * Onsets are in time order and so are presses, so the window of presses that
 * could answer an onset only ever moves forward. Walking it with two indices
 * costs the number of presses each onset can actually see instead of the
 * whole capture.
 *
 * It matters at exactly one place in the journey, and it is the one this was
 * written for: a unit is 12 attacks, a whole piece is 531 in the median and
 * **7 448 at the corpus maximum**. Comparing every press to every onset there
 * is ~55 million comparisons on the main thread, immediately after a pass
 * that already lasted several minutes.
 */
function honouredOnsets(
  groups: readonly PracticeGroup[],
  presses: readonly RecordedPress[],
  at: readonly number[],
  clock: RunClock,
  ignorePitch: boolean,
): readonly boolean[] {
  const window = HONOURED_BEATS * clock.beatMs;
  const out: boolean[] = new Array(groups.length).fill(false);
  let lo = 0;
  let hi = 0;

  for (let i = 0; i < groups.length; i += 1) {
    const t = at[i];

    while (lo < presses.length && presses[lo].tMs < t - window) {
      lo += 1;
    }

    while (hi < presses.length && presses[hi].tMs <= t + window) {
      hi += 1;
    }

    for (let p = lo; p < hi; p += 1) {
      if (ignorePitch || groups[i].midis.includes(presses[p].midi)) {
        out[i] = true;
        break;
      }
    }
  }

  return out;
}

// ── Notes played that the score never asked for ──────────────────

/**
 * A key struck where the passage asks for nothing of that pitch.
 *
 * ## Why this is not the gate that was removed
 *
 * The old validator matched every press to **the nearest group of the same
 * pitch** and then judged the delay. That attribution is what sank it: 22.8%
 * of notes repeat a pitch within a beat, so an ordinary jitter re-pointed a
 * press at a neighbouring group and the pass failed for a delay nobody had
 * played — 98.5% of its false failures contained no out-of-window error at
 * all.
 *
 * Nothing is attributed here. The question asked of a press is not *which*
 * note it was meant to be, it is whether the passage asks for that pitch
 * **anywhere** in the beat around it. A press that answers no onset was not
 * mis-timed, it was a different key — which is a fact about the keyboard, and
 * one no amount of jitter can manufacture.
 *
 * It is the exact mirror of `honouredOnsets`, read from the presses instead
 * of from the onsets, and it shares its window (`HONOURED_BEATS`) so the two
 * halves of the reading can never contradict each other.
 *
 * ## What it still cannot see
 *
 * A right note played at the wrong moment, as long as the right moment is
 * within a beat. That is deliberate: this step grades *pitch*, and timing is
 * reported by `pulse` and by `honoured`. Grading both from one number is how
 * the previous gate came to pass 62.2% of players with no rhythm at all.
 */

/**
 * Walks presses against onsets, in time order, once.
 *
 * Stateful because it is used twice over the same rule: live, as the player
 * strikes keys, and again over the whole capture when the pass is read. Two
 * implementations of "is this key asked for here" would drift, and the first
 * symptom would be a counter on screen disagreeing with the verdict under it.
 *
 * Both pointers only ever move forward, so a pass over a whole piece — 531
 * onsets in the median, 7 448 at the corpus maximum — costs the onsets each
 * press can actually see rather than the product of the two.
 */
export class StrayWatch {
  private readonly at: readonly number[];
  private readonly window: number;

  private lo = 0;
  private hi = 0;

  constructor(
    private readonly groups: readonly PracticeGroup[],
    at: readonly number[],
    clock: RunClock,
  ) {
    this.at = at;
    this.window = HONOURED_BEATS * clock.beatMs;
  }

  /** `true` when the passage asks for no such pitch around `tMs`. */
  isStray(midi: number, tMs: number): boolean {
    if (this.at.length === 0) {
      return false;
    }

    // Outside the passage there is nothing to be wrong about. This is what
    // keeps the count-in free: a player settling their hands on the keys
    // before the pass has not played a wrong note, they have not played.
    if (
      tMs < this.at[0] - this.window ||
      tMs > this.at[this.at.length - 1] + this.window
    ) {
      return false;
    }

    while (this.lo < this.at.length && this.at[this.lo] < tMs - this.window) {
      this.lo += 1;
    }

    while (this.hi < this.at.length && this.at[this.hi] <= tMs + this.window) {
      this.hi += 1;
    }

    for (let i = this.lo; i < this.hi; i += 1) {
      if (this.groups[i].midis.includes(midi)) {
        return false;
      }
    }

    return true;
  }
}

/**
 * Every press the passage never asked for.
 *
 * Empty when pitch is not being read — the tapped pass is played on whatever
 * key falls under the hand, so there is no such thing as a wrong one there.
 */
export function strayPresses(
  groups: readonly PracticeGroup[],
  presses: readonly RecordedPress[],
  at: readonly number[],
  clock: RunClock,
  ignorePitch: boolean,
): readonly RecordedPress[] {
  if (ignorePitch || groups.length === 0) {
    return [];
  }

  const watch = new StrayWatch(groups, at, clock);

  return presses.filter((press) => watch.isStray(press.midi, press.tMs));
}

/**
 * How many stray keys a passage may carry and still count as played straight.
 *
 * Proportional, because the object is not: a unit is 12 attacks and a whole
 * piece 531. The floor is what keeps it humane at the short end — one key
 * caught on the way past is what learning a passage sounds like, and a rule
 * that stopped the tempo dead on it would stop it on almost every pass.
 */
const STRAY_TOLERANCE_RATIO = 0.05;
const STRAY_TOLERANCE_FLOOR = 1;

export function strayTolerance(onsetCount: number): number {
  return Math.max(
    STRAY_TOLERANCE_FLOOR,
    Math.round(onsetCount * STRAY_TOLERANCE_RATIO),
  );
}

/**
 * Whether the passage came out under the right keys.
 *
 * This is what the tempo ladder is gated on, and it is **not** what
 * `wentThrough` is gated on: the two say different things and the step needs
 * both. Crossing the passage without stopping is the thing being taught, and
 * it is still an achievement with four keys caught on the way. Going *faster*
 * is a different claim, and it is the one a passage full of wrong notes has
 * not earned.
 */
export function playedClean(reading: RunReading): boolean {
  return reading.strays.length <= strayTolerance(reading.honoured.length);
}

/**
 * What to say about the stray keys, or nothing.
 *
 * Counted against the attacks and not on its own: "4 notes à côté" is a
 * different report on a unit of twelve and on a piece of five hundred, and
 * the player is the one who has to tell them apart.
 */
export function strayLine(reading: RunReading): string | null {
  const count = reading.strays.length;

  if (count === 0) {
    return null;
  }

  const total = reading.honoured.length;

  if (count === 1) {
    return `Une note à côté, sur ${total} attaques.`;
  }

  return `${count} notes à côté, sur ${total} attaques.`;
}

/**
 * What to say about the attacks nobody answered, or nothing.
 *
 * The mirror of `strayLine`, and it exists for the pass that has nothing to
 * mirror it *with*. Skipping a third of the notes without ever touching a
 * wrong key leaves no stray to name and no stop to name, so the card's note
 * fell straight through to `pulseLine`: a player who had just answered six
 * attacks of nineteen, evenly, read « Tu tiens le tempo. » The nineteen
 * cells of the run map were drawn hollow underneath it.
 *
 * The hollow cells stay the report — they say *where*, which no sentence
 * does. This is only what keeps the silence around them from reading as
 * approval.
 *
 * Passive on purpose, and no second person. `honoured` is measured with a ±1
 * beat window that never consumes a press (see `honouredOnsets`), so a hole
 * is a fact about the capture and never an accusation about the player.
 */
export function holesLine(reading: RunReading): string | null {
  const total = reading.honoured.length;
  const count = total - reading.honoured.filter(Boolean).length;

  if (count === 0) {
    return null;
  }

  if (count === 1) {
    return `Une attaque n’a pas été jouée, sur ${total}.`;
  }

  return `${count} attaques n’ont pas été jouées, sur ${total}.`;
}

/**
 * The first stretch of silence the score was asking notes for.
 *
 * Pitch is deliberately not consulted. A player hunting for the right key and
 * hitting the wrong one has not stopped — they are doing exactly what the
 * instruction asks. What ends a pass is the hands leaving the keyboard, and
 * that is a fact about *time only*. This is also what makes the reading
 * immune to the attribution artefact that sank the old validator: no press is
 * ever matched to a note here.
 *
 * ## An onset the reading answered was not gone past in silence
 *
 * The gap between two presses is bounded *by those two presses*, and an onset
 * that one of them answered was struck — hands on the keys, at that instant.
 * Counting it as missed charges the same press twice: it both answers the
 * note and opens the hole.
 *
 * Measured on a real pass, that is not a corner. A junction of 16 attacks
 * over 19 beats, **every one of them answered on its own key**, came back
 * "tu t'es arrêté à la mesure 3". The passage runs through half notes, so a
 * beat of ordinary anticipation on one of them stretches a press gap past two
 * beats with two onsets inside it — and the chaining step's only way out
 * closes on a pass that did exactly what it asked. The reading was
 * contradicting itself: `honoured` said 16 of 16 while `interruptions` said
 * the player had left.
 *
 * So the silence is counted in onsets **the reading did not record an answer
 * for**, by the very array it hands back. One source of truth, and the
 * contradiction stops being possible rather than becoming rarer.
 *
 * This does not put pitch back into the verdict. What opens a silence is
 * still the press stream and nothing else — a player hunting for keys and
 * finding wrong ones fills it, and is never read as having stopped. Pitch
 * only ever *exonerates* here, so nothing it says can create a stop.
 *
 * On a tapped pass (`ignorePitch`) the answer is a fact about time alone, so
 * the exoneration widens to a beat either side of the hole and a genuine stop
 * has to be about five beats long to read. That is the honest reading of a
 * mode where any key answers any note, and it errs toward the player.
 */
export function findInterruptions(
  groups: readonly PracticeGroup[],
  presses: readonly RecordedPress[],
  at: readonly number[],
  clock: RunClock,
  ignorePitch = false,
): readonly Interruption[] {
  if (groups.length === 0) {
    return [];
  }

  const silence = STOP_SILENCE_BEATS * clock.beatMs;
  // The same answer the reading publishes as `honoured`. See the note above.
  const answered = honouredOnsets(groups, presses, at, clock, ignorePitch);
  const last = at[at.length - 1];
  // Bookends: the run starts at its anchor and is not over until its final
  // onset has had its window. Without them a player who never begins, or who
  // gives up on the last bar, shows no gap at all.
  //
  // Anything struck before the anchor is dropped: the capture is emptied when
  // the pass is armed, so it can hold count-in presses, and a mark earlier
  // than the opening bookend would break the ascending order this scan walks.
  // It cannot end a silence inside the run either — the first gap starts at
  // the anchor by construction.
  const marks = [
    clock.anchorMs,
    ...presses.filter((p) => p.tMs >= clock.anchorMs).map((p) => p.tMs),
    last + silence,
  ];
  const out: Interruption[] = [];
  // Gaps are walked in time order and so are the onsets, so this pointer only
  // ever moves forward — the scan is linear, not quadratic.
  let g = 0;

  for (let i = 0; i < marks.length - 1; i += 1) {
    const from = marks[i];
    const to = marks[i + 1];

    // The opening bookend owns its own instant — a unit whose first onset
    // falls on the downbeat has it *inside* the silence of a player who never
    // came in. Every other mark is a press, and an onset landing on a press
    // was answered by it.
    const strictlyAfter = i > 0;

    while (g < groups.length && (strictlyAfter ? at[g] <= from : at[g] < from)) {
      g += 1;
    }

    if (to - from <= silence) {
      continue;
    }

    let missed = 0;
    let firstMissed = -1;

    for (let j = g; j < groups.length && at[j] < to; j += 1) {
      if (answered[j]) {
        continue;
      }

      missed += 1;

      if (firstMissed < 0) {
        firstMissed = j;
      }
    }

    if (missed < STOP_MISSED_ONSETS) {
      continue;
    }

    // Named on the first onset nobody answered, not on the first one the
    // window happens to open on: that is where the passage actually went
    // quiet, and it is what the panel sends the player back to.
    out.push({
      groupIndex: firstMissed,
      startTick: groups[firstMissed].startTick,
    });
  }

  return out;
}

/** The first place the player stopped, or `null`. */
export function findInterruption(
  groups: readonly PracticeGroup[],
  presses: readonly RecordedPress[],
  at: readonly number[],
  clock: RunClock,
): Interruption | null {
  return findInterruptions(groups, presses, at, clock)[0] ?? null;
}

// ── The player's own pulse ────────────────────────────────────────

/** How the player's beat compares to the one they were given. */
export type PulseReading = {
  /**
   * Their beat over the metronome's. Above 1 they are playing slower than the
   * click, below 1 faster.
   */
  readonly ratio: number;
  /** Spread around their own beat, as a fraction of it. Lower is steadier. */
  readonly spread: number;
  /** Onsets the reading rests on. */
  readonly samples: number;
};

/** Below this many honoured onsets, there is no pulse to read. */
const MIN_PULSE_SAMPLES = 6;

/** A spread at or under this reads as steady. */
const STEADY_SPREAD = 0.1;

/** Beyond this the player is not on the click's tempo, they are on their own. */
const DRIFT_RATIO = 0.06;

/**
 * Fit the player's presses to the instants they were aiming at.
 *
 * The slope of that fit is their tempo and the residual is their steadiness —
 * both **relative to their own beat**, never to the absolute clock. Playing
 * evenly a touch under tempo is the single most encouraging thing a beginner
 * can do, and the old validator's absolute window failed it 29.8% of the time
 * at 5% slow while passing a player with no rhythm at all.
 */
export function readPulse(
  groups: readonly PracticeGroup[],
  presses: readonly RecordedPress[],
  at: readonly number[],
  clock: RunClock,
  ignorePitch = false,
): PulseReading | null {
  const window = HONOURED_BEATS * clock.beatMs;
  const xs: number[] = [];
  const ys: number[] = [];
  // Same windowed walk as `honouredOnsets`, for the same reason: a pass over
  // a whole piece runs to thousands of onsets and as many presses.
  let lo = 0;
  let hi = 0;

  for (let i = 0; i < groups.length; i += 1) {
    const t = at[i];

    while (lo < presses.length && presses[lo].tMs < t - window) {
      lo += 1;
    }

    while (hi < presses.length && presses[hi].tMs <= t + window) {
      hi += 1;
    }

    let best: number | null = null;

    for (let p = lo; p < hi; p += 1) {
      const press = presses[p];

      if (!ignorePitch && !groups[i].midis.includes(press.midi)) {
        continue;
      }

      const distance = Math.abs(press.tMs - t);

      if (best === null || distance < Math.abs(best - t)) {
        best = press.tMs;
      }
    }

    if (best === null) {
      continue;
    }

    xs.push(t);
    ys.push(best);
  }

  if (xs.length < MIN_PULSE_SAMPLES) {
    return null;
  }

  const meanX = mean(xs);
  const meanY = mean(ys);
  let sxx = 0;
  let sxy = 0;

  for (let i = 0; i < xs.length; i += 1) {
    sxx += (xs[i] - meanX) ** 2;
    sxy += (xs[i] - meanX) * (ys[i] - meanY);
  }

  if (sxx <= 0) {
    return null;
  }

  const ratio = sxy / sxx;

  if (!Number.isFinite(ratio) || ratio <= 0) {
    return null;
  }

  const intercept = meanY - ratio * meanX;
  let squared = 0;

  for (let i = 0; i < xs.length; i += 1) {
    squared += (ys[i] - (ratio * xs[i] + intercept)) ** 2;
  }

  const spread = Math.sqrt(squared / xs.length) / clock.beatMs;

  return { ratio, spread, samples: xs.length };
}

/**
 * How a finished pass reads to the journey.
 *
 * `cancelled` when the player stopped the transport themselves, which is not
 * an event about their playing at all.
 */
export function passOutcome(
  reading: RunReading | null,
): 'validated' | 'failed' | 'cancelled' {
  if (reading === null) {
    return 'cancelled';
  }

  return reading.interruptions.length === 0 ? 'validated' : 'failed';
}

/**
 * What to say about the pulse, or nothing.
 *
 * Three outcomes and a default of silence, the rule the whole journey runs
 * on. Nothing is ever said about an unsteady pass: the player already knows,
 * and a line naming it would cost them the one thing they came for. The run
 * map has already shown them where it went.
 *
 * **`STEADY_SPREAD` was calibrated on six seconds and is now also read over
 * three minutes.** Over a whole piece the residual around the fitted line
 * absorbs rubato, phrase breathing and the piece's own rhythmic complexity,
 * so it may well never clear the threshold there. That is a tolerable failure
 * — the function says nothing rather than something wrong — and it is
 * measurable rather than guessable: `run-report-logger` already stamps
 * `pulseSpread` on every pass, phase 2 included, behind
 * `localStorage.setItem('virtuosa:journey:run-log', '1')`. Ten real
 * traversals settle it.
 */
export function pulseLine(
  reading: PulseReading | null,
  /**
   * `false` once the click is off. Naming the metronome to someone who
   * cannot hear one is simply false, and the sentence is the same fact
   * either way: they are steady, a little under or over the tempo.
   */
  withMetronome = true,
): string | null {
  if (reading === null || reading.spread > STEADY_SPREAD) {
    return null;
  }

  const reference = withMetronome ? 'du métronome' : 'du tempo';

  if (reading.ratio > 1 + DRIFT_RATIO) {
    return `Tu joues régulièrement, un peu en dessous ${reference}.`;
  }

  if (reading.ratio < 1 - DRIFT_RATIO) {
    return `Tu joues régulièrement, un peu devant ${reference}.`;
  }

  return 'Tu tiens le tempo.';
}

function mean(values: readonly number[]): number {
  let total = 0;

  for (const value of values) {
    total += value;
  }

  return total / values.length;
}

// ── Bars ──────────────────────────────────────────────────────────

/**
 * The bar of the unit a tick falls in, 1-based, or `null` when the unit does
 * not start on a bar line.
 *
 * The number is only offered when it is true. Work units are cut on bar
 * lines, but the first unit of a phrase inherits the phrase's own start, and
 * a phrase may open on an upbeat — naming "bar 3" of a unit whose bars are
 * offset from the score's would send the player to the wrong place.
 */
export function barOfTick(
  tick: number,
  unitStartTick: number,
  ticksPerBar: number,
): number | null {
  if (ticksPerBar <= 0 || unitStartTick % ticksPerBar !== 0) {
    return null;
  }

  return Math.floor((tick - unitStartTick) / ticksPerBar) + 1;
}

/** How the interruption is named. Never a reproach, never a count. */
export function interruptionLine(
  interruption: Interruption,
  unitStartTick: number,
  ticksPerBar: number,
): string {
  const bar = barOfTick(interruption.startTick, unitStartTick, ticksPerBar);

  if (bar === null) {
    return 'Tu t’es arrêté en route.';
  }

  return `Tu t’es arrêté à la mesure ${bar}.`;
}

// ── The pulse row ─────────────────────────────────────────────────

/**
 * How many cells the pulse row draws for one bar.
 *
 * Compound metres are felt in their dotted beats — 6/8 is two, not six — and
 * the row shows what is *felt*, while the metronome keeps clicking the grid
 * it schedules. Measured, 9.4% of units (987 / 10 530) are in a compound
 * metre, which is also where the old `round(ticksPerBar / ppq)` put the
 * accent on the wrong beat.
 */
export function pulseCells(numerator: number, denominator: number): number {
  if (numerator <= 0) {
    return 4;
  }

  if (denominator === 8 && numerator % 3 === 0) {
    return numerator / 3;
  }

  return numerator;
}

/** Which cell of the row a metronome beat lights. */
export function pulseCellOf(
  beatInBar: number,
  numerator: number,
  denominator: number,
): number {
  const cells = pulseCells(numerator, denominator);

  if (cells <= 0) {
    return 0;
  }

  if (denominator === 8 && numerator % 3 === 0) {
    return Math.floor(beatInBar / 3) % cells;
  }

  return beatInBar % cells;
}

/**
 * What the panel says when the watchdog stopped a pass.
 *
 * Nothing about contexts or clocks: the player pressed and the app went
 * quiet, and what they need is the next act. Rejouer first, because after a
 * short interruption the next gesture wakes the sound back up; reloading
 * second, because an audio context Safari has left `interrupted` for good
 * comes back no other way.
 */
export function stallLine(stall: { readonly phase: "count_in" | "playback" }): string {
  const what = stall.phase === "count_in" ? "le décompte" : "la lecture";

  return `Le son ne répondait plus et ${what} s’est arrêté. Rejoue ; si rien ne part, recharge l’application.`;
}
